import React, { Component } from 'react';
import Responsive from 'react-responsive';
import PropTypes from 'prop-types';

import CustomScroll from 'react-custom-scroll';
import 'react-custom-scroll/dist/customScroll.css';

import Fuse from 'fuse.js';
import { findDOMNode } from 'react-dom';

import styles from './SeDropdownItemsList.scss';

import SeDropdownItem from './SeDropdownItem';
import SeDropdownNothingFoundItem from './SeDropdownNothingFoundItem';
import { DROPDOWN_PADDINGS } from '../../../config/consts';

import { detectIE } from '../../../utils/detectIE';

const Phones = props => <Responsive {...props} maxWidth={400} />;
const isIE = detectIE();

const FUSE_OPTIONS = {
    shouldSort: true,
    findAllMatches: true,
    threshold: 0.3,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    minMatchCharLength: 1,
    keys: ['text'],
};

export default class SeDropdownItemsList extends Component {
    constructor(props) {
        super(props);

        const { items, searchValue, withoutSorting } = props;

        const fuse = new Fuse(items, FUSE_OPTIONS);

        this.state = {
            items,
            fuse,
            searchValue,
            initialScrollPosition: undefined,
            filteredItems: SeDropdownItemsList.sortItems(
                searchValue ? fuse.search(searchValue) : items,
                withoutSorting,
            ),
        };
    }

    static sortItems = (items, withoutSorting = false) =>
        withoutSorting
            ? [...items]
            : [...items].sort((a, b) => {
                  const textA = a.text.toUpperCase();
                  const textB = b.text.toUpperCase();
                  if (textA < textB) {
                      return -1;
                  }
                  if (textA > textB) {
                      return 1;
                  }

                  return 0;
              });

    static getDerivedStateFromProps(nextProps, prevState) {
        let newState = null;
        let { fuse } = prevState;
        let doSearch = false;

        if (nextProps.items !== prevState.items) {
            newState = newState || {};
            newState.items = nextProps.items;
            fuse = new Fuse(nextProps.items, FUSE_OPTIONS);
            newState.fuse = fuse;
            doSearch = true;
        }

        if (nextProps.searchValue !== prevState.searchValue) {
            newState = newState || {};
            newState.searchValue = nextProps.searchValue;
            doSearch = true;
        }

        if (doSearch) {
            newState.filteredItems = SeDropdownItemsList.sortItems(
                nextProps.searchValue ? fuse.search(nextProps.searchValue) : nextProps.items,
                nextProps.withoutSorting,
            );
        }

        return newState;
    }

    componentDidMount() {
        const selectedEl = findDOMNode(this.selectedItem);
        const firstEl = findDOMNode(this.listFirstItem);
        const listTopOffset = firstEl ? firstEl.offsetTop : 0;

        if (this.props.value && selectedEl) {
            this.setState({
                initialScrollPosition: selectedEl.offsetTop - listTopOffset,
            });
        }
    }

    highlightMatched(text) {
        const searchValue = this.props.searchValue.toLowerCase();

        if (!searchValue) {
            return text;
        }

        const start = text.toLowerCase().indexOf(searchValue);
        const end = start + searchValue.length;

        if (start !== -1) {
            const prefix = text.substring(0, start);
            const highlighted = text.substring(start, end);
            const suffix = text.substring(end);
            return (
                <span>
                    {prefix}
                    <span className={styles.highlightedMatch}>{highlighted}</span>
                    {suffix}
                </span>
            );
        }

        return null;
    }

    includeInSearchResults = ({ value }) => {
        const { excludeFromSearch, searchValue } = this.props;

        return searchValue ? !excludeFromSearch.includes(value) : true;
    };

    renderList() {
        const { formatItemText, value, onSelect } = this.props;
        const { filteredItems } = this.state;

        const items = filteredItems.reduce((accum, filteredItem, index) => {
            if (!this.includeInSearchResults(filteredItem)) {
                return accum;
            }

            const item = filteredItem.item || filteredItem;
            const content = this.highlightMatched(formatItemText(item.text));

            if (content) {
                accum.push(
                    <SeDropdownItem
                        key={`key-${item.value}`}
                        value={item.value}
                        onSelect={onSelect}
                        selected={item.value === value}
                        ref={refEl => {
                            if (index === 0) {
                                this.listFirstItem = refEl;
                            }
                            if (item.value === value) {
                                this.selectedItem = refEl;
                            }
                        }}
                    >
                        {content}
                    </SeDropdownItem>,
                );
            }

            return accum;
        }, []);

        return <ul className={styles.itemsList}>{items.length ? items : <SeDropdownNothingFoundItem />}</ul>;
    }

    getDDHeight = isPhone => {
        const { infoBlock, dropdownHeight, infoBlockHeight } = this.props;
        const height = infoBlock ? dropdownHeight - infoBlockHeight : dropdownHeight;
        return isPhone ? 'auto' : `${height + DROPDOWN_PADDINGS}px`;
    };

    getDropdownStyles(isPhone) {
        const maxHeight = this.getDDHeight(isPhone);
        return {
            height: isIE ? maxHeight : 'auto',
            maxHeight,
            overflowY: 'hidden',
        };
    }

    render() {
        const { infoBlock } = this.props;

        return (
            <Phones>
                {isPhone => (
                    <React.Fragment>
                        <div className={styles.itemsListWrap} style={this.getDropdownStyles(isPhone)}>
                            <CustomScroll flex="1" scrollTo={this.state.initialScrollPosition}>
                                {this.renderList()}
                                {infoBlock && isPhone ? <div className={styles.infoBlock}>{infoBlock}</div> : null}
                            </CustomScroll>
                        </div>
                        {infoBlock && !isPhone ? <div className={styles.infoBlock}>{infoBlock}</div> : null}
                    </React.Fragment>
                )}
            </Phones>
        );
    }
}

SeDropdownItemsList.propTypes = {
    searchValue: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        }),
    ).isRequired,
    formatItemText: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    infoBlock: PropTypes.element,
    excludeFromSearch: PropTypes.array.isRequired,
    dropdownHeight: PropTypes.number.isRequired,
    infoBlockHeight: PropTypes.number.isRequired,
    withoutSorting: PropTypes.bool,
};

SeDropdownItemsList.defaultProps = {
    // infoBlock: null,
    withoutSorting: false,
};
